/**
 * ProfileEditPage Component
 * Edit own profile with all fields
 * Layout: Clean form on white card, role-adaptive fields
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';
import Navbar from '../components/Navbar';
import ImageUploader from '../components/ImageUploader';
import MultiImageUploader from '../components/MultiImageUploader';
import '../styles/colors.css';

const GENRE_LIST = [...new Set([
  'Ambient', 'Alternative', 'Blues', 'Classical', 'Country', 'Cultural',
  'Dance', 'EDM', 'Electronic', 'Emo', 'Experimental', 'Folk', 'Grunge',
  'Hardcore', 'Hip-hop', 'Instrumental', 'Jazz', 'Metal', 'Noise', 'Pop',
  'Psychedelic', 'Punk', 'R&B', 'Rap', 'Reggae-Ska', 'Religious', 'Rock',
  'Shoegaze', 'Singer-Songwriter', 'Spoken Word'
])].sort();

const VENUE_TYPES = ['bar', 'restaurant', 'club', 'diy', 'theater', 'house', 'other'];

const ProfileEditPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    display_name: '',
    username: '',
    email: '',
    city: '',
    state: '',
    bio: '',
    profile_image_url: '',
    website_url: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    show_email_publicly: false,
    // Password change fields
    current_password: '',
    new_password: '',
    confirm_password: '',
    // Venue-specific
    venue_type: [],
    capacity: '',
    age_restriction: '',
    venue_address: '',
    contact_method: '',
    gear_list: '',
    // Band-specific
    genre_tags: [],
    travel_distance: '',
    music_player_urls: ['', '', ''],
    // Photographer-specific
    photo_services: [],
    image_gallery_urls: [],
    // Vendor-specific
    product_types: '',
    // Studio-specific
    studio_services: [],
    price_hour: '',
    price_day: ''
  });

  // Venue/Promoter linking state
  const [linkedPromoters, setLinkedPromoters] = useState([]);
  const [linkedVenues, setLinkedVenues] = useState([]);
  const [showPromoterSearch, setShowPromoterSearch] = useState(false);
  const [showVenueSearch, setShowVenueSearch] = useState(false);
  const [promoterSearchQuery, setPromoterSearchQuery] = useState('');
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [promoterSearchResults, setPromoterSearchResults] = useState([]);
  const [venueSearchResults, setVenueSearchResults] = useState([]);

  useEffect(() => {
    if (user) {
      console.log('ProfileEditPage - User data:', user);
      console.log('Profile image URL:', user.profile_image_url);
      console.log('Gallery URLs:', user.image_gallery_urls);
      
      setFormData({
        display_name: user.display_name || '',
        username: user.username || '',
        email: user.email || '',
        city: user.city || '',
        state: user.state || '',
        bio: user.bio || '',
        profile_image_url: user.profile_image_url || '',
        website_url: user.website_url || '',
        instagram_url: user.instagram_url || '',
        facebook_url: user.facebook_url || '',
        tiktok_url: user.tiktok_url || '',
        show_email_publicly: user.show_email_publicly || false,
        current_password: '',
        new_password: '',
        confirm_password: '',
        venue_type: user.venue_type || [],
        capacity: user.capacity || '',
        age_restriction: user.age_restriction || '',
        venue_address: user.venue_address || '',
        contact_method: user.contact_method || '',
        gear_list: user.gear_list || '',
        genre_tags: user.genre_tags || [],
        travel_distance: user.travel_distance || '',
        music_player_urls: user.music_player_urls && user.music_player_urls.length > 0 
          ? [...user.music_player_urls, '', '', ''].slice(0, 3) 
          : ['', '', ''],
        photo_services: user.photo_services || [],
        image_gallery_urls: user.image_gallery_urls?.filter(Boolean) || [],
        product_types: user.product_types || '',
        studio_services: user.studio_services || [],
        price_hour: user.price_hour || '',
        price_day: user.price_day || ''
      });
    }
  }, [user]);

  // Fetch linked promoters/venues
  useEffect(() => {
    const fetchLinks = async () => {
      if (!user) return;

      try {
        if (user.account_role === 'venue') {
          const response = await apiClient.get(`/venue-promoter-links/venue/${user.id}/promoters`);
          if (response.data.success) {
            setLinkedPromoters(response.data.promoters || []);
          }
        } else if (user.account_role === 'promoter') {
          const response = await apiClient.get(`/venue-promoter-links/promoter/${user.id}/venues`);
          if (response.data.success) {
            setLinkedVenues(response.data.venues || []);
          }
        }
      } catch (err) {
        console.error('Failed to fetch links:', err);
      }
    };

    fetchLinks();
  }, [user]);

  // Debounced search for promoters
  useEffect(() => {
    if (!promoterSearchQuery || promoterSearchQuery.trim().length < 2) {
      setPromoterSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get(`/users/search-promoters?q=${encodeURIComponent(promoterSearchQuery)}`);
        if (response.data.success) {
          setPromoterSearchResults(response.data.users);
        }
      } catch (err) {
        console.error('Promoter search failed:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [promoterSearchQuery]);

  // Debounced search for venues
  useEffect(() => {
    if (!venueSearchQuery || venueSearchQuery.trim().length < 2) {
      setVenueSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await apiClient.get(`/users/search-venues?q=${encodeURIComponent(venueSearchQuery)}`);
        if (response.data.success) {
          setVenueSearchResults(response.data.users);
        }
      } catch (err) {
        console.error('Venue search failed:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [venueSearchQuery]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genre_tags: prev.genre_tags.includes(genre)
        ? prev.genre_tags.filter(g => g !== genre)
        : [...prev.genre_tags, genre]
    }));
  };

  const handleVenueTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      venue_type: prev.venue_type.includes(type)
        ? prev.venue_type.filter(t => t !== type)
        : [...prev.venue_type, type]
    }));
  };

  // Add promoter link (for venues)
  const handleAddPromoter = async (promoter) => {
    try {
      const response = await apiClient.post('/venue-promoter-links/venue/promoters', {
        promoter_id: promoter.id
      });
      
      if (response.data.success) {
        setLinkedPromoters([...linkedPromoters, promoter]);
        setPromoterSearchQuery('');
        setPromoterSearchResults([]);
        setShowPromoterSearch(false);
        setSuccess('Promoter linked successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link promoter');
    }
  };

  // Remove promoter link (for venues)
  const handleRemovePromoter = async (promoterId) => {
    try {
      const response = await apiClient.delete(`/venue-promoter-links/venue/promoters/${promoterId}`);
      
      if (response.data.success) {
        setLinkedPromoters(linkedPromoters.filter(p => p.id !== promoterId));
        setSuccess('Promoter link removed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove promoter link');
    }
  };

  // Add venue link (for promoters)
  const handleAddVenue = async (venue) => {
    try {
      const response = await apiClient.post('/venue-promoter-links/promoter/venues', {
        venue_id: venue.id
      });
      
      if (response.data.success) {
        setLinkedVenues([...linkedVenues, venue]);
        setVenueSearchQuery('');
        setVenueSearchResults([]);
        setShowVenueSearch(false);
        setSuccess('Venue linked successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link venue');
    }
  };

  // Remove venue link (for promoters)
  const handleRemoveVenue = async (venueId) => {
    try {
      const response = await apiClient.delete(`/venue-promoter-links/promoter/venues/${venueId}`);
      
      if (response.data.success) {
        setLinkedVenues(linkedVenues.filter(v => v.id !== venueId));
        setSuccess('Venue link removed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove venue link');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate password change if attempting
      if (formData.new_password) {
        if (!formData.current_password) {
          setError('Current password required to change password');
          setLoading(false);
          return;
        }
        if (formData.new_password !== formData.confirm_password) {
          setError('New passwords do not match');
          setLoading(false);
          return;
        }
      }

      // Geocode if city/state changed
      let updateData = { ...formData };
      
      // Remove password fields if not changing password
      if (!updateData.new_password) {
        delete updateData.current_password;
        delete updateData.new_password;
        delete updateData.confirm_password;
      }
      
      // Remove username and email (backend might not allow changing these)
      delete updateData.username;
      delete updateData.email;
      
      // Only include role-specific fields for the user's actual role
      const userRole = user.account_role;
      
      if (userRole !== 'band') {
        delete updateData.genre_tags;
        delete updateData.music_player_urls;
      }
      
      if (userRole !== 'venue') {
        delete updateData.venue_type;
        delete updateData.capacity;
        delete updateData.age_restriction;
        delete updateData.venue_address;
        delete updateData.gear_list;
      }
      
      if (userRole !== 'photographer') {
        delete updateData.photo_services;
        // Don't delete image_gallery_urls - multiple roles can have galleries
      }
      
      // Keep image_gallery_urls for bands, venues, photographers, vendors, studios
      if (!['band', 'venue', 'photographer', 'vendor', 'studio'].includes(userRole)) {
        delete updateData.image_gallery_urls;
      }
      
      if (userRole !== 'vendor') {
        delete updateData.product_types;
      }
      
      if (userRole !== 'studio') {
        delete updateData.studio_services;
        delete updateData.price_hour;
        delete updateData.price_day;
      }
      
      if (!['band', 'photographer', 'vendor', 'studio', 'promoter', 'fan', 'venue'].includes(userRole)) {
  delete updateData.travel_distance;
}
      
      // Log email toggle for debugging
      console.log('show_email_publicly value:', updateData.show_email_publicly);
      
      // Convert empty string numbers to null and parse valid numbers
      const numberFields = ['capacity', 'travel_distance', 'price_hour', 'price_day'];
      numberFields.forEach(field => {
        if (updateData[field] !== undefined) {
          if (updateData[field] === '' || updateData[field] === null || updateData[field] === undefined) {
            delete updateData[field]; // Don't send empty fields
          } else {
            const parsed = parseInt(updateData[field]);
            if (isNaN(parsed)) {
              delete updateData[field]; // Don't send if can't parse
            } else {
              updateData[field] = parsed;
            }
          }
        }
      });
      
      if (formData.city && formData.state) {
        if (formData.city !== user.city || formData.state !== user.state) {
          try {
            const geocodeResult = await geocodeCityState(formData.city, formData.state);
            updateData.latitude = geocodeResult.latitude;
            updateData.longitude = geocodeResult.longitude;
          } catch (geocodeError) {
            setError(geocodeError.message);
            setLoading(false);
            return;
          }
        }
      }

      // Clean up music player URLs (remove empty strings)
      if (updateData.music_player_urls) {
        updateData.music_player_urls = updateData.music_player_urls.filter(url => url && url.trim() !== '');
        if (updateData.music_player_urls.length === 0) {
          delete updateData.music_player_urls;
        }
      }
      
      // Clean up arrays - remove if empty
      const arrayFields = ['genre_tags', 'venue_type', 'photo_services', 'studio_services'];
      arrayFields.forEach(field => {
        if (updateData[field] !== undefined) {
          if (!Array.isArray(updateData[field]) || updateData[field].length === 0) {
            delete updateData[field];
          }
        }
      });

      console.log('Gallery URLs being sent:', updateData.image_gallery_urls);
      console.log('Sending update data:', updateData);

      const response = await apiClient.put('/users/profile', updateData);

      console.log('Profile update response:', response.data);

      if (response.data.success) {
        setError(''); // Clear any previous errors
        setSuccess('Profile updated successfully!');
        
        // Update AuthContext and localStorage with new user data
        try {
          console.log('Updating user with:', response.data.user);
          updateUser(response.data.user);
          console.log('User updated successfully');
        } catch (updateErr) {
          console.error('Failed to update user context:', updateErr);
        }
        
        // Navigate back to profile (no reload needed)
        setTimeout(() => {
          console.log('Navigating to profile...');
          navigate('/profile');
        }, 1000);
      }
    } catch (err) {
      console.error('Update profile error:', err);
      console.error('Error response:', err.response?.data);
      setSuccess(''); // Clear any success message
      const errorMessage = err.response?.data?.error || 'Failed to update profile';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)', padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: '18px' }}>Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .edit-form-input {
            font-size: 16px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-form-textarea {
            font-size: 16px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-form-select {
            font-size: 16px !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .edit-form-card {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '40px', paddingLeft: '20px', paddingRight: '20px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          {/* Page Title */}
          <h1 style={{ color: 'var(--text-light)', marginBottom: '24px', fontSize: '32px', fontWeight: '700' }}>
            Edit Profile
          </h1>

          {/* Form Card */}
          <form onSubmit={handleSubmit}>
            <div className="edit-form-card" style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>

              {/* Error/Success Messages */}
              {error && (
                <div style={{ padding: '12px', background: 'var(--accent-danger)', color: 'var(--text-light)', borderRadius: '6px', marginBottom: '20px' }}>
                  {error}
                </div>
              )}
              {success && (
                <div style={{ padding: '12px', background: 'var(--accent-primary)', color: 'var(--text-dark)', borderRadius: '6px', marginBottom: '20px' }}>
                  {success}
                </div>
              )}

              {/* Account Role (Read-Only) */}
              <div className="edit-form-section" style={{ marginBottom: '24px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  Account Type (Cannot Change)
                </label>
                <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '6px', fontSize: '15px', color: 'var(--text-dark)' }}>
                  {user.account_role.charAt(0).toUpperCase() + user.account_role.slice(1)}
                </div>
              </div>

              {/* Display Name */}
              <div style={{ marginBottom: '20px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Display Name *
                </label>
                <input
                  type="text"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleChange}
                  required
                  className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                />
              </div>

              {/* Username */}
              <div style={{ marginBottom: '20px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                />
              </div>

              {/* Email */}
              <div style={{ marginBottom: '20px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                />
              </div>

              {/* Email Visibility Toggle */}
              <div className="edit-form-section" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="show_email_publicly"
                    checked={formData.show_email_publicly}
                    onChange={handleChange}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                    Show email publicly on my profile
                  </span>
                </label>
              </div>

              {/* Password Change Section */}
              <div style={{ marginBottom: '32px', padding: '20px', background: '#f9f9f9', borderRadius: '6px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                  Change Password (Optional)
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="current_password"
                    value={formData.current_password}
                    onChange={handleChange}
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleChange}
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>

                <div>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>
              </div>

              {/* City & State */}
              <div className="edit-form-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>
                <div>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    maxLength="2"
                    placeholder="CT"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px', textTransform: 'uppercase' }}
                  />
                </div>
              </div>

              {/* Bio */}
              <div style={{ marginBottom: '20px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Bio
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows="4"
                  maxLength="1000"
                  className="edit-form-textarea" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>

              {/* Profile Image */}
              <div style={{ marginBottom: '20px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Profile Image
                </label>
                <ImageUploader
                  currentImageUrl={formData.profile_image_url}
                  onImageUploaded={(url) => setFormData({ ...formData, profile_image_url: url })}
                  folder="profiles"
                />
              </div>

              {/* Website */}
              <div style={{ marginBottom: '20px' }}>
                <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  Website
                </label>
                <input
                  type="url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                />
              </div>

              {/* Social Links */}
              <div className="edit-form-section" style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: 'var(--text-dark)' }}>
                  Social Media
                </h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <input
                    type="url"
                    name="instagram_url"
                    value={formData.instagram_url}
                    onChange={handleChange}
                    placeholder="Instagram URL"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                  <input
                    type="url"
                    name="facebook_url"
                    value={formData.facebook_url}
                    onChange={handleChange}
                    placeholder="Facebook URL"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                  <input
                    type="url"
                    name="tiktok_url"
                    value={formData.tiktok_url}
                    onChange={handleChange}
                    placeholder="TikTok URL"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>
              </div>

              {/* ROLE-SPECIFIC FIELDS */}

              {/* BAND FIELDS */}
              {user.account_role === 'band' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Genres * (Select 1-5)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      {GENRE_LIST.map(genre => (
                        <label key={genre} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.genre_tags.includes(genre)}
                            onChange={() => handleGenreToggle(genre)}
                            style={{ marginRight: '6px' }}
                          />
                          <span style={{ fontSize: '14px', color: 'var(--text-dark)' }}>{genre}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Travel Distance (miles) *
                    </label>
                    <input
                      type="number"
                      name="travel_distance"
                      value={formData.travel_distance}
                      onChange={handleChange}
                      required
                      min="0"
                      className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                    />
                  </div>

                  {/* BAND GALLERY */}
                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Image Gallery (Max 10, Optional)
                    </label>
                    <MultiImageUploader
                      currentImages={formData.image_gallery_urls}
                      onImagesChange={(urls) => setFormData({ ...formData, image_gallery_urls: urls })}
                      folder="gallery"
                      maxImages={10}
                    />
                  </div>
                </>
              )}

              {/* VENUE FIELDS */}
              {user.account_role === 'venue' && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Venue Type * (Select 1-2)
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                      {VENUE_TYPES.map(type => (
                        <label key={type} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.venue_type.includes(type)}
                            onChange={() => handleVenueTypeToggle(type)}
                            style={{ marginRight: '6px' }}
                          />
                          <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                        Capacity *
                      </label>
                      <input
                        type="number"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleChange}
                        required
                        min="0"
                        className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                      />
                    </div>
                    <div>
                      <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                        Age Restriction *
                      </label>
                      <select
                        name="age_restriction"
                        value={formData.age_restriction}
                        onChange={handleChange}
                        required
                        className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                      >
                        <option value="">Select...</option>
                        <option value="all_ages">All Ages</option>
                        <option value="18_plus">18+</option>
                        <option value="21_plus">21+</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Venue Address
                    </label>
                    <input
                      type="text"
                      name="venue_address"
                      value={formData.venue_address}
                      onChange={handleChange}
                      className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Gear Available
                    </label>
                    <textarea
                      name="gear_list"
                      value={formData.gear_list}
                      onChange={handleChange}
                      rows="3"
                      placeholder="PA system, drum kit, etc."
                      className="edit-form-textarea" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>

                  {/* VENUE GALLERY */}
                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Image Gallery (Max 10, Optional)
                    </label>
                    <MultiImageUploader
                      currentImages={formData.image_gallery_urls}
                      onImagesChange={(urls) => setFormData({ ...formData, image_gallery_urls: urls })}
                      folder="gallery"
                      maxImages={10}
                    />
                  </div>

                  {/* LINKED PROMOTERS */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                        Linked Promoters
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPromoterSearch(!showPromoterSearch)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          background: 'var(--accent-primary)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '700'
                        }}
                      >
                        {showPromoterSearch ? 'Cancel' : '+ Add Promoter'}
                      </button>
                    </div>

                    {/* Currently Linked Promoters */}
                    {linkedPromoters.length > 0 && (
                      <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {linkedPromoters.map((promoter) => (
                          <div
                            key={promoter.id}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              background: 'var(--accent-primary)',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}
                          >
                            <span>{promoter.display_name || promoter.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemovePromoter(promoter.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--accent-danger)',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '700',
                                padding: '0',
                                lineHeight: '1'
                              }}
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Search Form */}
                    {showPromoterSearch && (
                      <div style={{ padding: '16px', background: 'var(--bg-light)', border: '1px solid var(--border-light)', borderRadius: '8px' }}>
                        <input
                          type="text"
                          placeholder="Search for promoters..."
                          value={promoterSearchQuery}
                          onChange={(e) => setPromoterSearchQuery(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid var(--border-light)',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            fontSize: '14px'
                          }}
                        />
                        {promoterSearchResults.length > 0 && (
                          <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'white' }}>
                            {promoterSearchResults.map(result => (
                              <div
                                key={result.id}
                                onClick={() => handleAddPromoter(result)}
                                style={{
                                  padding: '12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid var(--border-light)',
                                  color: 'var(--text-dark)',
                                  fontSize: '14px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'var(--smoke-gray)';
                                  e.target.style.color = 'var(--accent-primary)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'white';
                                  e.target.style.color = 'var(--text-dark)';
                                }}
                              >
                                <strong>{result.display_name}</strong>
                                <span style={{ marginLeft: '8px', fontSize: '13px', opacity: 0.7 }}>
                                  ({result.city}, {result.state})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {promoterSearchQuery.length >= 2 && promoterSearchResults.length === 0 && (
                          <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '14px', fontStyle: 'italic' }}>
                            No results found
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* VENUE DEFAULT SEARCH RADIUS */}
                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Default Search Radius (miles)
                    </label>
                    <input
                      type="number"
                      name="travel_distance"
                      value={formData.travel_distance}
                      onChange={handleChange}
                      min="0"
                      placeholder="25"
                      className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                    />
                    <small style={{ display: 'block', marginTop: '4px', fontSize: '13px', color: 'var(--text-muted)' }}>
                      Default radius for location-based searches on the site (not displayed publicly)
                    </small>
                  </div>
                </>
              )}

              {/* PHOTOGRAPHER/VENDOR FIELDS */}
              {['photographer', 'vendor'].includes(user.account_role) && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Travel Distance (miles) *
                  </label>
                  <input
                    type="number"
                    name="travel_distance"
                    value={formData.travel_distance}
                    onChange={handleChange}
                    required
                    min="0"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>
              )}

              {/* STUDIO FIELD */}
              {user.account_role === 'studio' && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Default Radius (miles) *
                  </label>
                  <input
                    type="number"
                    name="travel_distance"
                    value={formData.travel_distance}
                    onChange={handleChange}
                    required
                    min="0"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                </div>
              )}

              {/* PHOTOGRAPHER GALLERY */}
              {user.account_role === 'photographer' && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Image Gallery (Max 10)
                  </label>
                  <MultiImageUploader
                    currentImages={formData.image_gallery_urls}
                    onImagesChange={(urls) => setFormData({ ...formData, image_gallery_urls: urls })}
                    folder="gallery"
                    maxImages={10}
                  />
                </div>
              )}

              {/* VENDOR/STUDIO GALLERY (optional for these roles) */}
              {['vendor', 'studio'].includes(user.account_role) && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Image Gallery (Max 10, Optional)
                  </label>
                  <MultiImageUploader
                    currentImages={formData.image_gallery_urls}
                    onImagesChange={(urls) => setFormData({ ...formData, image_gallery_urls: urls })}
                    folder="gallery"
                    maxImages={10}
                  />
                </div>
              )}

              {/* PROMOTER FIELDS */}
              {user.account_role === 'promoter' && (
                <>
                  {/* LINKED VENUES */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                        Linked Venues
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowVenueSearch(!showVenueSearch)}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          background: 'var(--accent-primary)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '700'
                        }}
                      >
                        {showVenueSearch ? 'Cancel' : '+ Add Venue'}
                      </button>
                    </div>

                    {/* Currently Linked Venues */}
                    {linkedVenues.length > 0 && (
                      <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {linkedVenues.map((venue) => (
                          <div
                            key={venue.id}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              background: 'var(--accent-primary)',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}
                          >
                            <span>{venue.display_name || venue.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveVenue(venue.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--accent-danger)',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '700',
                                padding: '0',
                                lineHeight: '1'
                              }}
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Search Form */}
                    {showVenueSearch && (
                      <div style={{ padding: '16px', background: 'var(--bg-light)', border: '1px solid var(--border-light)', borderRadius: '8px' }}>
                        <input
                          type="text"
                          placeholder="Search for venues..."
                          value={venueSearchQuery}
                          onChange={(e) => setVenueSearchQuery(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid var(--border-light)',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            fontSize: '14px'
                          }}
                        />
                        {venueSearchResults.length > 0 && (
                          <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border-light)', borderRadius: '6px', background: 'white' }}>
                            {venueSearchResults.map(result => (
                              <div
                                key={result.id}
                                onClick={() => handleAddVenue(result)}
                                style={{
                                  padding: '12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid var(--border-light)',
                                  color: 'var(--text-dark)',
                                  fontSize: '14px',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'var(--smoke-gray)';
                                  e.target.style.color = 'var(--accent-primary)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'white';
                                  e.target.style.color = 'var(--text-dark)';
                                }}
                              >
                                <strong>{result.display_name}</strong>
                                <span style={{ marginLeft: '8px', fontSize: '13px', opacity: 0.7 }}>
                                  ({result.city}, {result.state})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {venueSearchQuery.length >= 2 && venueSearchResults.length === 0 && (
                          <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '14px', fontStyle: 'italic' }}>
                            No results found
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* PROMOTER TRAVEL DISTANCE */}
                  <div style={{ marginBottom: '20px' }}>
                    <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                      Travel Distance (miles)
                    </label>
                    <input
                      type="number"
                      name="travel_distance"
                      value={formData.travel_distance}
                      onChange={handleChange}
                      min="0"
                      placeholder="25"
                      className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                    />
                  </div>
                </>
              )}

              {/* FAN DEFAULT SEARCH RADIUS */}
              {user.account_role === 'fan' && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="edit-form-label" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)' }}>
                    Default Search Radius (miles)
                  </label>
                  <input
                    type="number"
                    name="travel_distance"
                    value={formData.travel_distance}
                    onChange={handleChange}
                    min="0"
                    placeholder="25"
                    className="edit-form-input" style={{ width: '100%', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '6px', fontSize: '15px' }}
                  />
                  <small style={{ display: 'block', marginTop: '4px', fontSize: '13px', color: 'var(--text-muted)' }}>
                    Default radius for location-based searches on the site (not displayed publicly)
                  </small>
                </div>
              )}

            </div>

            {/* Action Buttons */}
            <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="btn btn-secondary edit-form-button"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary edit-form-button"
                disabled={loading}
                style={{ minWidth: '120px' }}
              >
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ProfileEditPage;
